/**
 * The admin's image API: `${VITE_API_BASE_URL}/ocrservice/image` (savePicture behind the main API), called with the
 * signed-in user's Bearer and X-Api-Key, the same way the upload control does (SAM-949, Logic 15 Oct 2026).
 *
 * There is no default URL and no public key here: an environment is named by VITE_API_BASE_URL alone, and without it
 * or without a session nothing is sent (ImageApiConfigError). The public registration pages do not use this: they
 * have their own client with the tenant's public key (api/publicApi).
 */
export class ImageApiConfigError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ImageApiConfigError';
  }
}

/** True when a request can be made: the base URL is configured and there is a session. */
export const imageApiReady = (): boolean => !!import.meta.env.VITE_API_BASE_URL && !!localStorage.getItem('token');

/** `${base}/ocrservice/image` plus the query. Throws when the base URL is not configured. */
export const imageApiUrl = (query = ''): string => {
  const base = import.meta.env.VITE_API_BASE_URL;
  if (!base) throw new ImageApiConfigError('VITE_API_BASE_URL is not configured');
  return `${base}/ocrservice/image${query ? `?${query}` : ''}`;
};

/**
 * Bearer of the session and the business (or build) API key. `json` adds the JSON content type; a FormData upload
 * leaves it out so the browser writes the multipart boundary. Throws when there is no session.
 */
export const imageApiHeaders = (options: { json?: boolean } = {}): Record<string, string> => {
  const token = localStorage.getItem('token');
  if (!token) throw new ImageApiConfigError('There is no session to authorize the request');
  let apiKey = import.meta.env.VITE_API_KEY || '';
  try {
    const bd = sessionStorage.getItem('businessData');
    if (bd) apiKey = JSON.parse(bd).api_key || apiKey;
  } catch {
    // an unreadable businessData keeps the build's key
  }
  return {
    ...(options.json ? { 'Content-Type': 'application/json' } : {}),
    Authorization: `Bearer ${token}`,
    'X-Api-Key': apiKey,
  };
};

/** The key of an uploaded image, wherever the response puts it. */
export const uploadedImageKey = (data: any): string | undefined =>
  data?.data?.picture || data?.image_key || data?.data?.image_key || data?.data?.key || undefined;
